// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { Button } from "@insite/mobius/index";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import { mergeWithCss } from "@werner/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
    // XNG-Changes
    showUnitOfMeasure?: boolean;
    showAddToCartAndQty?: boolean;
    view?: "List" | "Grid";
    extendedStyles?: ProductListActionsStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    qtyUomWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    viewOptionsButton?: ButtonPresentationProps;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
            &:empty {
                display: none;
            }
        `,
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    // XNG-Changes
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-top: 10px;
            gap: 8px;
            &.has-uom {
                flex-direction: column;
            }
        `,
    },
    quantityOrdered: {
        sizeVariant: "small",
        cssOverrides: {
            formField: css`
                width: 100%;
                & input:not(.react-datetime-picker__inputGroup__input) {
                    &,
                    &:focus {
                        padding: 2px 20px;
                        text-align: center;
                    }
                }
                label {
                    display: none;
                }
            `,
        },
    },
    unitOfMeasureSelect: {
        sizeVariant: "small",
        cssOverrides: {
            formField: css`
                width: 100%;
                & select:not(.react-datetime-picker__inputGroup__input) {
                    &,
                    &:focus {
                        padding: 2px 20px;
                        text-align: center;
                    }
                }
                label {
                    display: none;
                }
            `,
        },
    },
    qtyUomWrapper: {
        css: css`
            width: 100%;
            display: flex;
            gap: 10px;
        `,
    },
    addToCartButton: {
        css: css`
            &&& {
                width: 100%;
                max-width: 100%;
            }
        `,
    },
    viewOptionsButton: {
        css: css`
            width: 100%;
        `,
    },
};

const ProductListActions: FC<Props> = ({
    productContext,
    productSettings,
    showUnitOfMeasure,
    showPrice,
    showAddToList,
    showAddToCartAndQty,
    view,
    isAuthenticated,
    extendedStyles,
}) => {
    const history = useHistory();
    const [styles] = useState(() => mergeWithCss(actionsStyles, extendedStyles));
    const productUnitOfMeasureSelect = (
        <>{showUnitOfMeasure && <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />}</>
    );

    // XNG-Changes
    const uoms = productContext.product.unitOfMeasures;
    const isVariantParent = productContext.product.isVariantParent;
    const handleViewOptionsClick = () => {
        history.push(productContext.productInfo.productDetailPath);
    };

    const shouldShowUom =
        showUnitOfMeasure &&
        productSettings?.alternateUnitsOfMeasure &&
        uoms &&
        uoms.filter(uom => uom.unitOfMeasure).length > 1;

    const productQuantityOrdered = (
        <>{showAddToCartAndQty && <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} />}</>
    );
    const productAddToCartButton = (
        <>
            {showAddToCartAndQty && (
                <ProductAddToCartButton
                    data-test-selector={`actionsAddToCart${productContext.product.id}`}
                    extendedStyles={styles.addToCartButton}
                />
            )}
        </>
    );

    return (
        <StyledWrapper {...styles.wrapper}>
            {isAuthenticated && showPrice && !isVariantParent && (
                <>
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={styles.price}
                    />
                    <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                </>
            )}
            {view === "List" && (
                <>
                    <StyledWrapper {...styles.qtyUomWrapper}>
                        {productUnitOfMeasureSelect}
                        {productQuantityOrdered}
                    </StyledWrapper>
                    {productAddToCartButton}
                </>
            )}
            {/* XNG-Changes */}
            {view === "Grid" && showAddToCartAndQty && !shouldShowUom && (
                <StyledWrapper {...styles.addToCartWrapper}>
                    {productQuantityOrdered}
                    {productAddToCartButton}
                </StyledWrapper>
            )}
            {view === "Grid" && showAddToCartAndQty && shouldShowUom && (
                <StyledWrapper {...styles.addToCartWrapper} className="has-uom">
                    <StyledWrapper {...styles.qtyUomWrapper}>
                        {productUnitOfMeasureSelect}
                        {productQuantityOrdered}
                    </StyledWrapper>
                    {productAddToCartButton}
                </StyledWrapper>
            )}
            {showAddToCartAndQty && isVariantParent && (
                <StyledWrapper {...styles.addToCartWrapper} className="has-uom">
                    <Button onClick={handleViewOptionsClick} {...styles.viewOptionsButton}>
                        {translate("View Options")}
                    </Button>
                </StyledWrapper>
            )}
            {isAuthenticated && showAddToList && (
                <StyledWrapper {...styles.addToListWrapper} data-test-selector="addToListWrapper">
                    <ProductAddToListLink
                        data-test-selector={`actionsAddToList${productContext.product.id}`}
                        extendedStyles={styles.addToListLink}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

ProductListActions.defaultProps = {
    view: "Grid",
    showAddToCartAndQty: true,
    showUnitOfMeasure: true,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductListActions));
